import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

const formatCurrency = (val) =>
  `₹${Number(val).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

export default function OvertimeTracking({ data, onDrillDown }) {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const ot = data?.overtimeSummary;
  const branchWise = ot?.branchWise || [];

  const chartData = branchWise.map((b) => ({
    name: b.branch,
    "OT Hours": b.otHours || 0,
    "SP Days": b.shiftPaisaDays || 0,
  }));

  const statCards = [
    {
      label: "Total OT Hours",
      value: ot?.totalOtHours ?? "-",
      suffix: "hrs",
      color: "#1565C0",
      bg: "linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)",
    },
    {
      label: "Total OT Cost",
      value: ot?.totalOtCost != null ? formatCurrency(ot.totalOtCost) : "-",
      color: "#EF6C00",
      bg: "linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)",
    },
    {
      label: "Shift Paisa Days",
      value: ot?.totalShiftPaisaDays ?? "-",
      suffix: "days",
      color: "#6A1B9A",
      bg: "linear-gradient(135deg, #F3E5F5 0%, #E1BEE7 100%)",
    },
    {
      label: "Shift Paisa Total",
      value:
        ot?.totalShiftPaisaAmount != null
          ? formatCurrency(ot.totalShiftPaisaAmount)
          : "-",
      color: "#2E7D32",
      bg: "linear-gradient(135deg, #E8F5E9 0%, #C8E6C9 100%)",
    },
  ];

  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: "16px",
        p: 2,
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        height: "100%",
      }}
    >
      <Typography
        sx={{ fontWeight: 700, fontSize: "16px", color: "#333", mb: 1.5 }}
      >
        Overtime & Shift Paisa
      </Typography>

      {/* Stat cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
          gap: 1.5,
          mb: 2,
        }}
      >
        {statCards.map((s, i) => (
          <Box
            key={i}
            onClick={() => onDrillDown && onDrillDown(null)}
            sx={{
              background: s.bg,
              borderRadius: "12px",
              p: 1.5,
              textAlign: "center",
              cursor: "pointer",
              transition: "all 0.2s",
              "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.12)" },
            }}
          >
            <Typography sx={{ fontSize: "11px", color: "#666" }}>
              {s.label}
            </Typography>
            <Typography
              sx={{ fontSize: "16px", fontWeight: 700, color: s.color }}
            >
              {s.value}
              {s.suffix && (
                <Typography
                  component="span"
                  sx={{ fontSize: "11px", fontWeight: 400, ml: 0.3 }}
                >
                  {s.suffix}
                </Typography>
              )}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Chart */}
      {chartData.length > 0 ? (
        <ResponsiveContainer width="100%" height={isSmall ? 180 : 220}>
          <BarChart
            data={chartData}
            margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis
              dataKey="name"
              tick={{ fontSize: isSmall ? 9 : 11, fill: "#666" }}
              angle={isSmall ? -20 : 0}
              textAnchor="end"
            />
            <YAxis
              yAxisId="left"
              tick={{ fontSize: 10, fill: "#666" }}
              label={{
                value: "Hours",
                angle: -90,
                position: "insideLeft",
                fontSize: 10,
                fill: "#1565C0",
              }}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              tick={{ fontSize: 10, fill: "#666" }}
              label={{
                value: "Days",
                angle: 90,
                position: "insideRight",
                fontSize: 10,
                fill: "#6A1B9A",
              }}
            />
            <Tooltip contentStyle={{ fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar
              yAxisId="left"
              dataKey="OT Hours"
              fill="#1565C0"
              barSize={isSmall ? 15 : 24}
              radius={[4, 4, 0, 0]}
              onClick={(d) => onDrillDown && onDrillDown(d?.name)}
              style={{ cursor: "pointer" }}
            />
            <Bar
              yAxisId="right"
              dataKey="SP Days"
              fill="#6A1B9A"
              barSize={isSmall ? 15 : 24}
              radius={[4, 4, 0, 0]}
              onClick={(d) => onDrillDown && onDrillDown(d?.name)}
              style={{ cursor: "pointer" }}
            />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <Typography textAlign="center" color="textSecondary" sx={{ py: 4 }}>
          No overtime data available for this month.
        </Typography>
      )}
    </Box>
  );
}
